import { useEffect, useMemo, useState } from "react";
import {
  Sparkles, Loader2, Wand2, Download, Heart, CheckCircle2, RefreshCw,
  Trash2, X, Share2, ChevronLeft, ChevronRight, AlertCircle,
} from "lucide-react";
import { orgApi } from "../lib/organizerApi";

const STYLE_LABELS = {
  premium: { vi: "Cao cấp", en: "Premium / Luxury", subtitle: "Sang trọng · vàng & navy" },
  clean:   { vi: "Tối giản", en: "Modern / Clean",   subtitle: "Trắng · chuyên nghiệp" },
  social:  { vi: "Sôi động", en: "High Energy",      subtitle: "Gradient · social-first" },
};

const PLATFORM_LABELS = {
  website_hero:    "Website hero (1920×1080)",
  facebook_cover:  "Facebook cover",
  facebook_event:  "Facebook event",
  linkedin_post:   "LinkedIn post",
  instagram_post:  "Instagram post (1:1)",
  instagram_story: "Instagram story (9:16)",
  tiktok_reels:    "TikTok / Reels (9:16)",
};

const ORDERED_PLATFORMS = [
  "website_hero", "facebook_cover", "facebook_event", "linkedin_post",
  "instagram_post", "instagram_story", "tiktok_reels",
];

/**
 * AI Banner / Creative Studio — Phase 6.3.
 *
 * Props:
 *  - eventSlug:   string  (required)
 *  - eventName:   string  (display only)
 *  - onSelected?: (creative) => void  invoked after a banner is selected
 */
export default function EventBannerStudio({ eventSlug, eventName, onSelected }) {
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [regenStyle, setRegenStyle] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null); // { creative, platform }
  const [toast, setToast] = useState("");

  const flash = (m) => { setToast(m); setTimeout(() => setToast(""), 2200); };

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await orgApi.get(`/organizer/events/${eventSlug}/creatives`);
      setCreatives(data.creatives || []);
    } catch (e) {
      setError(e?.response?.data?.detail || "Không tải được banner.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (eventSlug) load(); /* eslint-disable-next-line */ }, [eventSlug]);

  const generate = async (styles) => {
    setError("");
    if (styles && styles.length === 1) setRegenStyle(styles[0]);
    else setGenerating(true);
    try {
      const payload = styles ? { styles } : {};
      const { data } = await orgApi.post(`/organizer/events/${eventSlug}/creatives/generate`, payload);
      flash(styles ? `Đã tạo lại phong cách ${STYLE_LABELS[styles[0]]?.vi || styles[0]}` : "Đã tạo 3 phong cách banner");
      // Merge: keep untouched concepts and replace any regenerated styles
      const newOnes = data.creatives || [];
      const newStyles = new Set(newOnes.map((c) => c.style));
      setCreatives((prev) => {
        const kept = prev.filter((c) => !newStyles.has(c.style));
        // Newest first by created_at
        const combined = [...newOnes, ...kept];
        combined.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        return combined;
      });
    } catch (e) {
      setError(e?.response?.data?.detail || "Không tạo được banner. Vui lòng thử lại.");
    } finally {
      setGenerating(false);
      setRegenStyle(null);
    }
  };

  const select = async (c) => {
    setBusyAction(`select-${c.id}`);
    try {
      const { data } = await orgApi.post(`/organizer/events/${eventSlug}/creatives/${c.id}/select`);
      setCreatives((prev) => prev.map((x) => ({ ...x, selected: x.id === c.id })));
      flash("Đã chọn làm banner sự kiện. Trang công khai đã được cập nhật.");
      if (onSelected) onSelected(data.creative);
    } catch (e) {
      flash(e?.response?.data?.detail || "Không thể chọn banner.");
    } finally { setBusyAction(null); }
  };

  const favorite = async (c) => {
    setBusyAction(`fav-${c.id}`);
    try {
      const { data } = await orgApi.post(`/organizer/events/${eventSlug}/creatives/${c.id}/favorite`);
      setCreatives((prev) => prev.map((x) => x.id === c.id ? { ...x, favorited: data.creative.favorited } : x));
    } catch (_e) { /* ignore */ }
    finally { setBusyAction(null); }
  };

  const remove = async (c) => {
    if (!window.confirm("Xoá banner này?")) return;
    setBusyAction(`del-${c.id}`);
    try {
      await orgApi.delete(`/organizer/events/${eventSlug}/creatives/${c.id}`);
      setCreatives((prev) => prev.filter((x) => x.id !== c.id));
      flash("Đã xoá banner.");
    } catch (_e) {
      flash("Không thể xoá.");
    } finally { setBusyAction(null); }
  };

  const downloadAsset = (dataUrl, filename) => {
    const a = document.createElement("a");
    a.href = dataUrl; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const concepts = useMemo(() => {
    // Show ONE row per style, newest first
    const byStyle = new Map();
    for (const c of creatives) {
      if (!byStyle.has(c.style) || new Date(c.created_at) > new Date(byStyle.get(c.style).created_at)) {
        byStyle.set(c.style, c);
      }
    }
    const out = Array.from(byStyle.values());
    out.sort((a, b) => Object.keys(STYLE_LABELS).indexOf(a.style) - Object.keys(STYLE_LABELS).indexOf(b.style));
    return out;
  }, [creatives]);

  const hasNoConcepts = !loading && concepts.length === 0;

  return (
    <section data-testid="banner-studio" className="rounded-3xl bg-white border border-espresso/10 p-5 sm:p-6">
      <header className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.28em] text-espresso-muted font-semibold flex items-center gap-1.5">
            <Wand2 className="w-3 h-3" /> AI Creative Studio
          </div>
          <h2 className="mt-1 text-xl sm:text-2xl font-serif text-espresso">
            Tạo banner AI cho sự kiện
          </h2>
          <p className="mt-1 text-sm text-espresso-soft">
            AI sẽ tạo 3 phong cách banner (cao cấp · tối giản · sôi động) cho{" "}
            <span className="font-semibold text-espresso">{eventName || "sự kiện của bạn"}</span>.
            Mỗi phong cách bao gồm 7 kích thước sẵn sàng cho web & mạng xã hội.
          </p>
        </div>
      </header>

      {error && (
        <div data-testid="banner-studio-error" className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-800 flex items-center gap-2">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="py-12 text-center text-espresso-soft" data-testid="banner-studio-loading">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          <p className="mt-3 text-sm">Đang tải banner…</p>
        </div>
      ) : hasNoConcepts ? (
        <EmptyState onGenerate={() => generate(null)} busy={generating} />
      ) : (
        <>
          <div className="grid md:grid-cols-3 gap-4 sm:gap-5" data-testid="banner-concepts-grid">
            {concepts.map((c) => (
              <ConceptCard
                key={c.id}
                concept={c}
                isRegenerating={regenStyle === c.style}
                isBusy={busyAction}
                onSelect={() => select(c)}
                onFavorite={() => favorite(c)}
                onRegenerate={() => generate([c.style])}
                onDelete={() => remove(c)}
                onPreview={(platform) => setPreview({ creative: c, platform })}
                onDownload={downloadAsset}
              />
            ))}
            {Object.keys(STYLE_LABELS).filter((s) => !concepts.find((c) => c.style === s)).map((s) => (
              <MissingStylePlaceholder key={s} style={s} onCreate={() => generate([s])} busy={regenStyle === s} />
            ))}
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <button
              type="button"
              data-testid="banner-studio-regen-all"
              onClick={() => generate(null)}
              disabled={generating}
              className="inline-flex items-center justify-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.22em] font-semibold disabled:opacity-60"
            >
              {generating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Tạo lại tất cả
            </button>
            <p className="text-xs text-espresso-muted">
              Tạo lại sẽ thay thế ảnh hiện tại. Banner đang được chọn sẽ vẫn được dùng trên trang công khai cho đến khi bạn chọn cái khác.
            </p>
          </div>
        </>
      )}

      {preview && (
        <PreviewModal
          creative={preview.creative}
          startingPlatform={preview.platform}
          onClose={() => setPreview(null)}
          onDownload={downloadAsset}
          eventSlug={eventSlug}
        />
      )}

      {toast && (
        <div data-testid="banner-studio-toast" className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-espresso text-cream rounded-full px-4 py-2 text-sm shadow-lg z-50">
          {toast}
        </div>
      )}
    </section>
  );
}

function EmptyState({ onGenerate, busy }) {
  return (
    <div data-testid="banner-studio-empty" className="rounded-2xl border border-dashed border-espresso/20 bg-linen-100/40 p-6 sm:p-8 text-center">
      <div className="mx-auto w-12 h-12 rounded-2xl bg-espresso text-cream flex items-center justify-center">
        <Sparkles className="w-5 h-5" />
      </div>
      <h3 className="mt-3 font-serif text-xl text-espresso">Chưa có banner nào</h3>
      <p className="mt-1 text-sm text-espresso-soft max-w-md mx-auto">
        Bấm bên dưới để AI tạo 3 phong cách banner độc đáo cho sự kiện của bạn — kèm 7 kích thước sẵn sàng tải về.
      </p>
      <button
        type="button"
        onClick={onGenerate}
        disabled={busy}
        data-testid="banner-studio-generate"
        className="mt-5 inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-6 py-3 text-sm font-semibold disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
        {busy ? "AI đang sáng tạo…" : "Tạo banner AI"}
      </button>
      <p className="mt-2 text-[11px] text-espresso-muted">Có thể mất 20–40 giây.</p>
    </div>
  );
}

function ConceptCard({
  concept, isRegenerating, isBusy, onSelect, onFavorite, onRegenerate, onDelete, onPreview, onDownload,
}) {
  const label = STYLE_LABELS[concept.style] || { vi: concept.style };
  const hero = concept.assets?.website_hero;
  const isReady = concept.status === "ready";
  const failedMsg = concept.error_message;

  return (
    <article
      data-testid={`concept-${concept.style}`}
      className={`rounded-2xl border bg-white overflow-hidden flex flex-col transition-all ${
        concept.selected ? "border-emerald2 ring-2 ring-emerald2/30" : "border-espresso/10"
      }`}
    >
      <div className="aspect-[16/9] bg-linen-100/60 relative">
        {isRegenerating ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 backdrop-blur-sm">
            <Loader2 className="w-6 h-6 animate-spin text-espresso" />
            <span className="mt-2 text-xs text-espresso-soft">AI đang tạo lại…</span>
          </div>
        ) : isReady && hero ? (
          <button
            type="button"
            onClick={() => onPreview("website_hero")}
            data-testid={`concept-${concept.style}-preview`}
            className="absolute inset-0 group"
          >
            <img src={hero} alt={label.vi} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-charcoal/0 group-hover:bg-charcoal/40 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
              <span className="text-xs uppercase tracking-[0.24em] text-white font-semibold">Xem chi tiết</span>
            </div>
          </button>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-espresso-soft text-sm p-3 text-center">
            {failedMsg ? `Thất bại: ${failedMsg.slice(0, 80)}` : "Chưa có ảnh"}
          </div>
        )}
        {concept.selected && (
          <div className="absolute top-2 left-2 bg-emerald2 text-white text-[10px] uppercase tracking-[0.22em] font-semibold px-2 py-1 rounded-full flex items-center gap-1">
            <CheckCircle2 className="w-3 h-3" /> Đang dùng
          </div>
        )}
        {concept.favorited && (
          <div className="absolute top-2 right-2 bg-white/90 text-rose-500 rounded-full p-1.5">
            <Heart className="w-3.5 h-3.5 fill-rose-500" />
          </div>
        )}
      </div>

      <div className="p-4 flex-1 flex flex-col gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted font-semibold">{label.en}</div>
          <h4 className="font-serif text-lg text-espresso">{label.vi}</h4>
          <p className="text-xs text-espresso-muted">{label.subtitle}</p>
        </div>

        <div className="grid grid-cols-2 gap-1.5">
          <button
            type="button"
            onClick={onSelect}
            disabled={!isReady || isBusy === `select-${concept.id}` || concept.selected}
            data-testid={`concept-${concept.style}-select`}
            className="inline-flex items-center justify-center gap-1 bg-emerald2 text-white rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-semibold disabled:opacity-40 disabled:bg-warmgray-300"
          >
            <CheckCircle2 className="w-3 h-3" /> {concept.selected ? "Đã chọn" : "Chọn"}
          </button>
          <button
            type="button"
            onClick={onRegenerate}
            disabled={isRegenerating}
            data-testid={`concept-${concept.style}-regenerate`}
            className="inline-flex items-center justify-center gap-1 border border-espresso/15 text-espresso rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-semibold disabled:opacity-40"
          >
            <RefreshCw className="w-3 h-3" /> Tạo lại
          </button>
          <button
            type="button"
            onClick={onFavorite}
            data-testid={`concept-${concept.style}-favorite`}
            className="inline-flex items-center justify-center gap-1 border border-espresso/15 text-espresso rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-semibold"
          >
            <Heart className={`w-3 h-3 ${concept.favorited ? "fill-rose-500 text-rose-500" : ""}`} />
            {concept.favorited ? "Bỏ thích" : "Yêu thích"}
          </button>
          <button
            type="button"
            onClick={() => hero && onDownload(hero, `${concept.style}-website-hero.png`)}
            disabled={!isReady || !hero}
            data-testid={`concept-${concept.style}-download`}
            className="inline-flex items-center justify-center gap-1 border border-espresso/15 text-espresso rounded-full px-3 py-2 text-[10px] uppercase tracking-[0.22em] font-semibold disabled:opacity-40"
          >
            <Download className="w-3 h-3" /> Tải
          </button>
        </div>

        <div className="flex items-center justify-between gap-2 pt-1">
          <button
            type="button"
            onClick={() => onPreview("website_hero")}
            disabled={!isReady}
            className="text-[10px] uppercase tracking-[0.22em] text-espresso-soft hover:text-espresso underline-offset-2 hover:underline disabled:opacity-40"
          >
            7 kích thước
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`concept-${concept.style}-delete`}
            aria-label="Xoá"
            className="text-espresso-muted hover:text-red-600 p-1"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </article>
  );
}

function MissingStylePlaceholder({ style, onCreate, busy }) {
  const label = STYLE_LABELS[style] || { vi: style };
  return (
    <article className="rounded-2xl border border-dashed border-espresso/20 bg-linen-100/40 p-4 flex flex-col items-center justify-center text-center min-h-[280px]">
      <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted font-semibold">{label.en}</div>
      <h4 className="mt-0.5 font-serif text-lg text-espresso">{label.vi}</h4>
      <p className="mt-1 text-xs text-espresso-muted max-w-[14rem]">{label.subtitle}</p>
      <button
        type="button"
        onClick={onCreate}
        disabled={busy}
        data-testid={`concept-${style}-create`}
        className="mt-4 inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-semibold disabled:opacity-60"
      >
        {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />}
        Tạo phong cách này
      </button>
    </article>
  );
}

function PreviewModal({ creative, startingPlatform, onClose, onDownload, eventSlug }) {
  const available = ORDERED_PLATFORMS.filter((p) => creative.assets?.[p]);
  const startIdx = Math.max(0, available.indexOf(startingPlatform));
  const [idx, setIdx] = useState(startIdx);
  const platform = available[idx];
  const dataUrl = creative.assets?.[platform];
  const label = STYLE_LABELS[creative.style] || { vi: creative.style };

  const next = () => setIdx((i) => (i + 1) % available.length);
  const prev = () => setIdx((i) => (i - 1 + available.length) % available.length);

  const share = async () => {
    try {
      if (navigator.share) {
        await navigator.share({ title: `${label.vi} – ${PLATFORM_LABELS[platform]}`, text: "Banner AI" });
        return;
      }
    } catch (_e) { /* user cancel */ }
    try {
      await navigator.clipboard.writeText(dataUrl);
    } catch (_e) { /* ignore */ }
  };

  return (
    <div
      data-testid="banner-preview-modal"
      className="fixed inset-0 z-50 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-espresso/10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-espresso-muted font-semibold">{label.vi}</div>
            <div className="text-sm font-medium text-espresso truncate">{PLATFORM_LABELS[platform]}</div>
          </div>
          <button type="button" onClick={onClose} data-testid="banner-preview-close" aria-label="Đóng" className="text-espresso-muted hover:text-espresso">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 bg-warmgray-50 flex items-center justify-center p-5 relative">
          <img src={dataUrl} alt={PLATFORM_LABELS[platform]} className="max-w-full max-h-[60vh] object-contain shadow-xl" />
          {available.length > 1 && (
            <>
              <button onClick={prev} aria-label="Trước" className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md">
                <ChevronLeft className="w-4 h-4 text-espresso" />
              </button>
              <button onClick={next} aria-label="Sau" className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 shadow-md">
                <ChevronRight className="w-4 h-4 text-espresso" />
              </button>
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-espresso/10 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex flex-wrap gap-1.5 flex-1">
            {available.map((p, i) => (
              <button
                key={p}
                type="button"
                onClick={() => setIdx(i)}
                data-testid={`banner-preview-platform-${p}`}
                className={`text-[10px] uppercase tracking-[0.18em] rounded-full px-2.5 py-1 font-semibold ${
                  i === idx ? "bg-espresso text-cream" : "bg-linen-100 text-espresso-soft hover:bg-linen-100/70"
                }`}
              >
                {PLATFORM_LABELS[p]?.split("(")[0].trim() || p}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => onDownload(dataUrl, `${eventSlug}-${creative.style}-${platform}.png`)}
              data-testid="banner-preview-download"
              className="inline-flex items-center gap-1.5 bg-espresso text-cream rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold"
            >
              <Download className="w-3.5 h-3.5" /> Tải về
            </button>
            <button
              type="button"
              onClick={share}
              data-testid="banner-preview-share"
              className="inline-flex items-center gap-1.5 border border-espresso/20 text-espresso rounded-full px-4 py-2 text-xs uppercase tracking-[0.22em] font-semibold"
            >
              <Share2 className="w-3.5 h-3.5" /> Chia sẻ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
